import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import Layout from '@/components/Layout';
import { ListingsGridSkeleton } from '@/components/SkeletonLoaders';
import { 
  Search, Briefcase, Building2, User, MapPin, Clock, ArrowRight, X, 
  SlidersHorizontal, ArrowUpDown, Tag, Star 
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrowseListings = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    sectors: [],
    provinces: [],
    cities: [],
    location_types: ['remote', 'hybrid', 'onsite'],
    priorities: ['alta', 'media', 'bassa']
  });
  
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    sector: '',
    location_province: '',
    location_city: '',
    location_type: '',
    priority: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  });

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const res = await axios.get(`${API}/listings/filters/options`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFilterOptions(res.data);
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };
    if (token) fetchFilterOptions();
  }, [token]);

  // Fetch listings with filters
  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.sector) params.append('sector', filters.sector);
      if (filters.location_province) params.append('location_province', filters.location_province);
      if (filters.location_city) params.append('location_city', filters.location_city);
      if (filters.location_type) params.append('location_type', filters.location_type);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      if (filters.sort_order) params.append('sort_order', filters.sort_order);

      const res = await axios.get(`${API}/listings?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setListings(res.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
      toast.error('Errore nel caricamento degli annunci');
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    if (token) fetchListings();
  }, [token, fetchListings]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      sector: '',
      location_province: '',
      location_city: '',
      location_type: '',
      priority: '',
      sort_by: 'created_at',
      sort_order: 'desc'
    });
  };

  const activeFiltersCount = Object.entries(filters).filter(
    ([key, value]) => value && key !== 'sort_by' && key !== 'sort_order'
  ).length;

  const locationTypeLabels = {
    remote: 'Remoto',
    hybrid: 'Ibrido',
    onsite: 'In sede'
  };

  const priorityLabels = {
    alta: 'Alta Priorità',
    media: 'Media Priorità',
    bassa: 'Bassa Priorità'
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      alta: 'bg-red-100 text-red-700 border-red-200',
      media: 'bg-[#344B71]/10 text-[#344B71] border-[#344B71]/20',
      bassa: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    const labels = { alta: 'Urgente', media: 'Media', bassa: 'Bassa' };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[priority] || styles.media}`}>
        {labels[priority] || priority}
      </span>
    );
  };

  const getLocationTypeBadge = (locationType) => {
    if (!locationType) return null;
    const styles = {
      remote: 'bg-green-100 text-green-700',
      hybrid: 'bg-purple-100 text-purple-700',
      onsite: 'bg-blue-100 text-blue-700'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[locationType] || 'bg-slate-100'}`}>
        {locationTypeLabels[locationType] || locationType}
      </span>
    );
  };

  // Filter Tag Component
  const FilterTag = ({ label, onRemove }) => (
    <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#344B71]/10 text-[#344B71] rounded-full text-sm">
      {label}
      <button onClick={onRemove} className="hover:text-[#344B71] transition-colors">
        <X className="w-3 h-3" />
      </button>
    </span>
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-8 mt-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-playfair font-bold text-[#344B71]">Esplora Annunci</h1>
          <p className="text-slate-600 mt-1">
            {user?.user_type === 'professional' 
              ? 'Trova aziende che cercano i tuoi servizi'
              : 'Trova professionisti per le tue esigenze'}
          </p>
        </motion.div>

        {/* Search and Filters Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <Card className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Cerca per titolo, categoria, settore..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && fetchListings()}
                  className="pl-12 h-12 rounded-xl border-slate-200 focus:border-[#344B71]"
                />
              </div>
              
              {/* Filter Button */}
              <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-12 px-4 rounded-xl border-slate-200 hover:border-[#344B71] hover:bg-[#344B71]/5"
                  >
                    <SlidersHorizontal className="w-5 h-5 mr-2" />
                    Filtri
                    {activeFiltersCount > 0 && (
                      <span className="ml-2 w-5 h-5 bg-[#344B71] text-white text-xs rounded-full flex items-center justify-center">
                        {activeFiltersCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                
                <SheetContent className="w-full sm:max-w-md bg-white overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle className="text-[#344B71] flex items-center gap-2">
                      <SlidersHorizontal className="w-5 h-5" />
                      Filtri Avanzati
                    </SheetTitle>
                  </SheetHeader>
                  
                  <div className="space-y-6 mt-6">
                    {/* Category Filter */}
                    <div>
                      <Label className="flex items-center gap-2 text-slate-700 mb-2">
                        <Briefcase className="w-4 h-4" />
                        Categoria
                      </Label>
                      <Select
                        value={filters.category || 'all'}
                        onValueChange={(v) => handleFilterChange('category', v === 'all' ? '' : v)}
                      >
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="Tutte le categorie" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutte le categorie</SelectItem>
                          {filterOptions.categories.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Sector Filter */}
                    <div>
                      <Label className="flex items-center gap-2 text-slate-700 mb-2">
                        <Tag className="w-4 h-4" />
                        Settore
                      </Label>
                      <Select
                        value={filters.sector || 'all'}
                        onValueChange={(v) => handleFilterChange('sector', v === 'all' ? '' : v)}
                      >
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="Tutti i settori" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutti i settori</SelectItem>
                          {filterOptions.sectors.map(sec => (
                            <SelectItem key={sec} value={sec}>{sec}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Province Filter */}
                    <div>
                      <Label className="flex items-center gap-2 text-slate-700 mb-2">
                        <MapPin className="w-4 h-4" />
                        Provincia
                      </Label>
                      <Select
                        value={filters.location_province || 'all'}
                        onValueChange={(v) => handleFilterChange('location_province', v === 'all' ? '' : v)}
                      >
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="Tutte le province" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutte le province</SelectItem>
                          {filterOptions.provinces.map(prov => (
                            <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* City Filter */}
                    <div>
                      <Label className="flex items-center gap-2 text-slate-700 mb-2">
                        <Building2 className="w-4 h-4" />
                        Città
                      </Label>
                      <Select
                        value={filters.location_city || 'all'}
                        onValueChange={(v) => handleFilterChange('location_city', v === 'all' ? '' : v)}
                      >
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="Tutte le città" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutte le città</SelectItem>
                          {filterOptions.cities.map(city => (
                            <SelectItem key={city} value={city}>{city}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Work Mode Filter */}
                    <div>
                      <Label className="flex items-center gap-2 text-slate-700 mb-2">
                        <User className="w-4 h-4" />
                        Modalità di Lavoro
                      </Label>
                      <Select
                        value={filters.location_type || 'all'}
                        onValueChange={(v) => handleFilterChange('location_type', v === 'all' ? '' : v)}
                      >
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="Tutte le modalità" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutte le modalità</SelectItem>
                          {filterOptions.location_types.map(lt => (
                            <SelectItem key={lt} value={lt}>
                              {locationTypeLabels[lt] || lt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Priority Filter */}
                    <div>
                      <Label className="flex items-center gap-2 text-slate-700 mb-2">
                        <ArrowUpDown className="w-4 h-4" />
                        Priorità
                      </Label>
                      <Select
                        value={filters.priority || 'all'}
                        onValueChange={(v) => handleFilterChange('priority', v === 'all' ? '' : v)}
                      >
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="Tutte le priorità" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutte le priorità</SelectItem>
                          {filterOptions.priorities.map(p => (
                            <SelectItem key={p} value={p}>
                              {priorityLabels[p] || p}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Sort Options */}
                    <div className="pt-4 border-t border-slate-100">
                      <Label className="flex items-center gap-2 text-slate-700 mb-2">
                        <ArrowUpDown className="w-4 h-4" />
                        Ordina per
                      </Label>
                      <div className="grid grid-cols-2 gap-3">
                        <Select
                          value={filters.sort_by}
                          onValueChange={(v) => handleFilterChange('sort_by', v)}
                        >
                          <SelectTrigger className="rounded-xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="created_at">Data</SelectItem>
                            <SelectItem value="priority">Priorità</SelectItem>
                            <SelectItem value="title">Titolo</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <Select
                          value={filters.sort_order}
                          onValueChange={(v) => handleFilterChange('sort_order', v)}
                        >
                          <SelectTrigger className="rounded-xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="desc">Decrescente</SelectItem>
                            <SelectItem value="asc">Crescente</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="outline"
                        onClick={clearFilters}
                        className="flex-1 rounded-xl"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Resetta
                      </Button>
                      <Button
                        onClick={() => {
                          fetchListings();
                          setIsFilterOpen(false);
                        }}
                        className="flex-1 bg-[#344B71] hover:bg-[#344B71] text-white rounded-xl"
                      >
                        Applica Filtri
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
              
              {/* Search Button */}
              <Button
                onClick={fetchListings}
                className="h-12 px-6 bg-[#344B71] hover:bg-[#344B71] text-white rounded-xl"
              >
                <Search className="w-5 h-5 sm:mr-2" />
                <span className="hidden sm:inline">Cerca</span>
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* Active Filters Tags */}
        <AnimatePresence>
          {activeFiltersCount > 0 && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 flex flex-wrap gap-2"
            >
              {filters.search && (
                <FilterTag
                  label={`Cerca: "${filters.search}"`}
                  onRemove={() => handleFilterChange('search', '')}
                />
              )}
              {filters.category && (
                <FilterTag
                  label={`Categoria: ${filters.category}`}
                  onRemove={() => handleFilterChange('category', '')}
                />
              )}
              {filters.sector && (
                <FilterTag
                  label={`Settore: ${filters.sector}`}
                  onRemove={() => handleFilterChange('sector', '')}
                />
              )}
              {filters.location_province && (
                <FilterTag
                  label={`Provincia: ${filters.location_province}`}
                  onRemove={() => handleFilterChange('location_province', '')}
                />
              )}
              {filters.location_city && (
                <FilterTag
                  label={`Città: ${filters.location_city}`}
                  onRemove={() => handleFilterChange('location_city', '')}
                />
              )}
              {filters.location_type && (
                <FilterTag
                  label={`Modalità: ${locationTypeLabels[filters.location_type]}`}
                  onRemove={() => handleFilterChange('location_type', '')}
                />
              )}
              {filters.priority && (
                <FilterTag
                  label={`Priorità: ${priorityLabels[filters.priority]}`}
                  onRemove={() => handleFilterChange('priority', '')}
                />
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-slate-500 hover:text-red-500 text-sm"
              >
                Cancella tutti
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results Count */}
        <div className="mb-6 flex items-center justify-between">
          <p className="text-slate-600">
            <span className="font-semibold text-[#344B71]">{listings.length}</span> annunci trovati
          </p>
        </div>

        {/* Loading State - Skeleton Loaders */}
        {loading ? (
          <ListingsGridSkeleton count={6} />
        ) : listings.length === 0 ? (
          /* Empty State */
          <Card className="p-12 text-center">
            <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#344B71] mb-2">Nessun annuncio trovato</h3>
            <p className="text-slate-500 mb-6">Prova a modificare i filtri di ricerca</p>
            <Button onClick={clearFilters} variant="outline" className="rounded-full">
              Cancella filtri
            </Button>
          </Card>
        ) : (
          /* Listings Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((listing, index) => (
              <motion.div
                key={listing.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card 
                  className="h-full p-6 hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-[#344B71]/20 group"
                  onClick={() => navigate(`/listings/${listing.id}`)}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#344B71] to-[#344B71] rounded-xl flex items-center justify-center text-white">
                        {listing.listing_type === 'request' ? <Building2 className="w-5 h-5" /> : <User className="w-5 h-5" />}
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">
                          {listing.listing_type === 'request' ? 'Azienda cerca' : 'Professionista offre'}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {listing.priority && getPriorityBadge(listing.priority)}
                      {getLocationTypeBadge(listing.location_type)}
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-lg font-semibold text-[#344B71] mb-2 group-hover:text-[#344B71] transition-colors line-clamp-2">
                    {listing.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">{listing.description}</p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="px-3 py-1 bg-[#344B71]/10 text-[#344B71] text-xs font-medium rounded-full">
                      {listing.category}
                    </span>
                    {listing.sector && (
                      <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                        {listing.sector}
                      </span>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="flex items-center space-x-4 text-xs text-slate-500">
                      {listing.location_province && (
                        <span className="flex items-center">
                          <MapPin className="w-3 h-3 mr-1" />
                          {listing.location_city ? `${listing.location_city}, ` : ''}{listing.location_province}
                        </span>
                      )}
                      <span className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {new Date(listing.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
                      </span>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[#344B71] group-hover:translate-x-1 transition-all" />
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BrowseListings;
